import React, {useEffect, useMemo, useState} from "react";
import {
    Box,
    Button, SegmentedControl,
    Text,
    TextInput,
    Tooltip,
    UnstyledButton,
} from "@mantine/core";
import {IconEdit, IconHistory, IconPlus, IconSearch, IconArchive} from "@tabler/icons-react";
import {modals} from "@mantine/modals";
import cx from "clsx";
import dayjs from "dayjs";
import {useNeurotuneStore} from "@/common/store/neurotune";
import {ISubject} from "@/common/types";
import {
    createSubject as svcCreateSubject,
    loadArchivedSubjects,
    archiveSubject as svcArchiveSubject,
    loadSubjects,
    restoreSubject as svcRestoreSubject,
    updateSubject as svcUpdateSubject,
} from "@/common/services/subjectsService";
import {showErrorNotification, showSuccessNotification} from "@/common/notifications";
import {dayjsToDisplayString} from "@/common/utils/times";
import {NameModalBody} from "./NameModal";
import classes from "./SubjectsPanel.module.css";
import commonClasses from "@/common/common.module.css";
import {openConfirmModal} from "@/protocols/modals.tsx";

const isArchived = (s: ISubject) => s.state === "ARCHIVED";

export const SubjectsPanel: React.FC = () => {
    const activeCompany = useNeurotuneStore((state) => state.activeCompany);
    const loadSubjectsFromStore = useNeurotuneStore((state) => state.loadSubjects);
    const [subjects, setSubjects] = useState<ISubject[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [filterString, setFilterString] = useState("");
    const [showArchived, setShowArchived] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const loader = showArchived ? loadArchivedSubjects : loadSubjects;
        loader(activeCompany.id)
            .then((list) => {
                if (!cancelled) {
                    setSubjects(list);
                    setLoading(false);
                }
            })
            .catch((error: any) => {
                if (cancelled) return;
                setLoading(false);
                showErrorNotification({
                    title: "Failed to load subjects",
                    message: error?.response?.data?.message || "Could not load subjects.",
                });
            });
        return () => {
            cancelled = true;
        };
    }, [activeCompany.id, showArchived]);

    const filtered = useMemo(() => {
        const q = filterString.trim().toLowerCase();
        if (!q) return subjects;
        return subjects.filter(
            (s) => s.name?.toLowerCase().includes(q) || s.id.toLowerCase().includes(q)
        );
    }, [subjects, filterString]);
    const openArchiveModal = (subject: ISubject) => {
        if (saving) return;
        openConfirmModal({
            title: "Archive Subject",
            children: (
                <Text>
                    Are you sure you want to archive <Text span fw={700}>"{subject.name}"</Text>?
                </Text>
            ),
            labels: {confirm: "Archive", cancel: "Cancel"},
            confirmProps: {color: "red", loading: saving},
            onConfirm: () => runMutation({
                apiCall: () => svcArchiveSubject(activeCompany.id, subject.id).then(() => subject.id),
                optimisticUpdate: (archiveId, prev) => prev.filter((s) => s.id !== archiveId),
                successMessage: () => ({
                    title: "Subject Archived",
                    message: `Subject "${subject.name}" has been archived.`,
                }),
                failureTitle: "Failed to archive subject",
            }),
        });
    };
    const openRestoreModal = (subject: ISubject) => {
        if (saving) return;
        openConfirmModal({
            title: "Restore Subject",
            children: (
                <Text>
                    Are you sure you want to restore <Text span fw={700}>"{subject.name}"</Text>?
                </Text>
            ),
            labels: {confirm: "Restore", cancel: "Cancel"},
            confirmProps: {color: "blue", loading: saving},
            onConfirm: () => runMutation({
                apiCall: () => svcRestoreSubject(activeCompany.id, subject.id),
                optimisticUpdate: (restored, prev) => prev.filter((s) => s.id !== restored.id),
                successMessage: (restored) => ({
                    title: "Subject Restored",
                    message: `Subject "${restored.name}" has been restored.`,
                }),
                failureTitle: "Failed to restore subject",
            }),
        });
    }

    const runMutation = async <T, >({
                                        apiCall,
                                        optimisticUpdate,
                                        successMessage,
                                        failureTitle,
                                    }: {
        apiCall: () => Promise<T>;
        optimisticUpdate: (result: T, current: ISubject[]) => ISubject[];
        successMessage: (result: T) => { title: string; message: string };
        failureTitle: string;
    }): Promise<T | null> => {
        setSaving(true);
        try {
            const result = await apiCall();
            setSubjects((prev) => optimisticUpdate(result, prev));
            await loadSubjectsFromStore(activeCompany.id);
            showSuccessNotification(successMessage(result));
            return result;
        } catch (error: any) {
            showErrorNotification({
                title: failureTitle,
                message: error?.response?.data?.message ?? String(error),
            });
            return null;
        } finally {
            setSaving(false);
        }
    };

    const openEditModal = (subject: ISubject) => {
        if (saving) return;
        const modalId = `edit-subject-${subject.id}`;
        modals.open({
            modalId,
            title: "Edit Subject",
            size: "md",
            children: (
                <NameModalBody
                    initialName={subject.name}
                    onCancel={() => modals.close(modalId)}
                    loading={saving}
                    confirmTestId="confirm-edit-subject"
                    submitLabel="Confirm"
                    onConfirm={async (name) => {
                        const updated = await runMutation({
                            apiCall: () => svcUpdateSubject(activeCompany.id, subject.id, name),
                            optimisticUpdate: (u, prev) => prev.map((s) => (s.id === u.id ? u : s)),
                            successMessage: (u) => ({
                                title: "Subject Updated",
                                message: `Subject "${u.name}" has been updated.`,
                            }),
                            failureTitle: "Failed to update subject",
                        });
                        if (updated) modals.close(modalId);
                    }}
                />
            ),
        });
    };

    const openNewModal = () => {
        if (saving) return;
        const modalId = "new-subject";
        modals.open({
            modalId,
            title: "New Subject",
            size: "md",
            children: (
                <NameModalBody
                    onCancel={() => modals.close(modalId)}
                    loading={saving}
                    confirmTestId="confirm-create-subject"
                    submitLabel="Create"
                    onConfirm={async (name) => {
                        const created = await runMutation({
                            apiCall: () => svcCreateSubject(activeCompany.id, {name}),
                            optimisticUpdate: (c, prev) => [c, ...prev],
                            successMessage: (c) => ({
                                title: "Subject Created",
                                message: `Subject "${c.name}" has been created.`,
                            }),
                            failureTitle: "Failed to create subject",
                        });
                        if (created) modals.close(modalId);
                    }}
                />
            ),
        });
    };

    const renderRows = () => filtered.map((subject) => (
        <Box key={subject.id} data-testid={`subject-row-${subject.id}`}
             className={cx(commonClasses.hover, classes.row)}>
            <Box className={classes.rowActions}>
                {isArchived(subject) ? (
                    <Tooltip label="Restore Subject">
                        <UnstyledButton
                            data-testid={`restore-${subject.id}`}
                            onClick={() => openRestoreModal(subject)}
                        >
                            <IconHistory className={cx(classes.iconRestore, classes.icon)}/>
                        </UnstyledButton>
                    </Tooltip>
                ) : (
                    <>
                        <Tooltip label="Edit Subject">
                            <UnstyledButton
                                data-testid={`edit-${subject.id}`}
                                onClick={() => openEditModal(subject)}
                            >
                                <IconEdit className={cx(classes.iconEdit, classes.icon)}/>
                            </UnstyledButton>
                        </Tooltip>
                        <Tooltip label="Archive Subject">
                            <UnstyledButton
                                data-testid={`archive-${subject.id}`}
                                onClick={() => openArchiveModal(subject)}
                            >
                                <IconArchive className={cx(classes.iconArchive, classes.icon)}/>
                            </UnstyledButton>
                        </Tooltip>
                    </>
                )}
            </Box>
            <Box>
                <Text size="sm">{subject.id}</Text>
            </Box>
            <Box>
                <Text size="sm">{subject.name}</Text>
            </Box>
            <Box>
                <Text size="sm">{dayjsToDisplayString(dayjs(subject.createdAt))}</Text>
            </Box>
        </Box>
    ));

    return (
        <Box mr={"0.5rem"} ml={"0.25rem"} className={cx(classes.root, commonClasses.sbRadius)}>
            <Box className={cx(classes.toolbar)}>
                <TextInput
                    placeholder="search subjects"
                    aria-label="Search subjects"
                    rightSection={<IconSearch size={"1em"}/>}
                    onChange={(ev) => setFilterString(ev.currentTarget.value)}
                />
                <Box style={{display: "flex", justifyContent: "center"}}>
                    <SegmentedControl
                        className={cx(classes.showArchived)}
                        data={[
                            {label: "Active Subjects", value: "active"},
                            {label: "Archived Subjects", value: "archived"},
                        ]}
                        value={showArchived ? "archived" : "active"}
                        onChange={(ev) => setShowArchived(ev == "archived")}
                    />
                </Box>
                <Tooltip label="Cannot create while viewing archived subjects" disabled={!showArchived}>
                    <Button
                        style={{alignSelf: "end"}}
                        m={"0.5em"}
                        size="sm"
                        disabled={showArchived || saving}
                        leftSection={<IconPlus size={"1em"}/>}
                        onClick={openNewModal}
                    >
                        New Subject
                    </Button>
                </Tooltip>
            </Box>
            <Box className={cx(classes.table, commonClasses.tgtBorder)} mb={"1em"}>
                <Box className={cx(classes.header, commonClasses.blur)}>
                    <Box className={cx(classes.headerRow, commonClasses.tgbBorder)}>
                        <Box className={classes.rowActions}/>
                        <Box><Text fw={500} fz="sm">ID</Text></Box>
                        <Box><Text fw={500} fz="sm">Name</Text></Box>
                        <Box><Text fw={500} fz="sm">Created</Text></Box>
                    </Box>
                </Box>
                <Box className={cx(classes.scrollArea)}>
                    {loading ? (
                        <Box p="1em">
                            <Text>Loading…</Text>
                        </Box>
                    ) : subjects.length === 0 ? (
                        <Box p="2em" ta="center">
                            <Text c="dimmed">
                                {showArchived
                                    ? "No archived subjects."
                                    : "No subjects yet. Click \"New Subject\" to create one."}
                            </Text>
                        </Box>
                    ) : (
                        renderRows()
                    )}
                </Box>
                <Box className={cx(classes.footer, commonClasses.strongBlur, commonClasses.tgbBorder)}>
                    <Text ta={"end"}>
                        {filtered.length} {showArchived ? "Archived Subjects" : "Subjects"}
                    </Text>
                </Box>
            </Box>
        </Box>
    );
};